type ApiRecord = Record<string, unknown>;

export const PUBLIC_PROJECT_ID_PATTERN = /^pj_[0-9a-f]{32}$/;

export interface NormalizedProjectSession extends ApiRecord {
  id: string;
  agent_id: string;
  title: string;
  last_message_at: string;
  created_at: string;
}

export interface NormalizedProject extends ApiRecord {
  id: string;
  name: string;
  description: string | null;
  avatar_file_id: string | null;
  created_at: string;
  latest_chat_at: string | null;
  sessions: NormalizedProjectSession[];
}

export interface NormalizedUserProfile extends ApiRecord {
  id: string;
  email: string;
  username: string;
  avatar_file_id: string | null;
  signature_file_id: string | null;
  role: string;
}

export interface NormalizedFile extends ApiRecord {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  createdAt: string;
  fileUrl?: string;
}

export type NormalizedProjectArtifactSource = "project" | "chat" | "generated";

export interface NormalizedProjectArtifact extends ApiRecord {
  /** Artifact row primary key. Use this only with `/api/artifacts/:id`. */
  id: string;
  /** File row primary key. Use this only with `/api/files/:id/content`. */
  fileId: string | null;
  projectId: string | null;
  source: NormalizedProjectArtifactSource;
  name: string;
  mimeType: string | null;
  size: number | null;
  uploadedBy: string | null;
  createdAt: string;
  sessionId: string | null;
  agentId: string | null;
  artifactType: string | null;
}

function isRecord(value: unknown): value is ApiRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isApiEnvelope(value: unknown): value is ApiRecord & { data: unknown } {
  if (!isRecord(value) || !("data" in value)) return false;

  if (typeof value.success === "boolean") return true;

  // Also support older `{ data: ... }` / `{ data: ..., message: ... }`
  // envelopes, while preserving paginated payloads such as
  // `{ data: [...], total, page, limit }`.
  const envelopeKeys = new Set(["data", "message", "success", "statusCode"]);
  return Object.keys(value).every((key) => envelopeKeys.has(key));
}

/** Remove transport-level API envelopes without unwrapping domain `data` fields. */
export function unwrapApiData<T = unknown>(payload: unknown): T {
  let current = payload;
  let depth = 0;

  while (isApiEnvelope(current) && depth < 5) {
    current = current.data;
    depth += 1;
  }

  return current as T;
}

function stringValue(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return fallback;
}

function nullableString(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  return stringValue(value) || null;
}

function normalizeProjectSession(value: unknown): NormalizedProjectSession | null {
  if (!isRecord(value)) return null;

  const id = stringValue(value.id ?? value.sessionId ?? value.session_id);
  if (!id) return null;

  return {
    ...value,
    id,
    agent_id: stringValue(value.agent_id ?? value.agentId),
    title: stringValue(value.title),
    last_message_at: stringValue(value.last_message_at ?? value.lastMessageAt),
    created_at: stringValue(value.created_at ?? value.createdAt),
  };
}

/** Convert current Core camelCase projects and legacy snake_case projects to one UI model. */
export function normalizeProject(value: unknown): NormalizedProject | null {
  const unwrapped = unwrapApiData(value);
  if (!isRecord(unwrapped)) return null;

  const source = isRecord(unwrapped.project) ? unwrapped.project : unwrapped;
  const id = stringValue(source.id ?? source.publicId ?? source.public_id);
  if (!PUBLIC_PROJECT_ID_PATTERN.test(id)) return null;

  const rawSessions = Array.isArray(source.sessions) ? source.sessions : [];

  return {
    ...source,
    id,
    name: stringValue(source.name),
    description:
      source.description === null || source.description === undefined
        ? null
        : stringValue(source.description),
    avatar_file_id: nullableString(
      source.avatar_file_id ?? source.avatarFileId,
    ),
    created_at: stringValue(source.created_at ?? source.createdAt),
    latest_chat_at: nullableString(
      source.latest_chat_at ?? source.latestChatAt,
    ),
    sessions: rawSessions
      .map(normalizeProjectSession)
      .filter((session): session is NormalizedProjectSession => session !== null),
  };
}

/** Extract and normalize project arrays from legacy and current Core list responses. */
export function normalizeProjectList(payload: unknown): NormalizedProject[] {
  const unwrapped = unwrapApiData(payload);

  let values: unknown[] = [];
  if (Array.isArray(unwrapped)) {
    values = unwrapped;
  } else if (isRecord(unwrapped)) {
    if (Array.isArray(unwrapped.projects)) values = unwrapped.projects;
    else if (Array.isArray(unwrapped.items)) values = unwrapped.items;
    else if (Array.isArray(unwrapped.data)) values = unwrapped.data;
  }

  return values
    .map(normalizeProject)
    .filter((project): project is NormalizedProject => project !== null);
}

/** Normalize `/users/me` across the current Core and legacy web response shapes. */
export function normalizeUserProfile(payload: unknown): NormalizedUserProfile | null {
  const unwrapped = unwrapApiData(payload);
  if (!isRecord(unwrapped)) return null;

  const source = isRecord(unwrapped.user) ? unwrapped.user : unwrapped;
  const email = stringValue(source.email);
  const id = stringValue(source.id ?? source.userId ?? source.user_id);
  const username = stringValue(
    source.username ?? source.fullName ?? source.full_name,
    email.split("@")[0] || "User",
  );

  return {
    ...source,
    id,
    email,
    username,
    avatar_file_id: nullableString(
      source.avatar_file_id ?? source.avatarFileId,
    ),
    signature_file_id: nullableString(
      source.signature_file_id ?? source.signatureFileId,
    ),
    role: stringValue(source.role),
  };
}

/** Normalize `/files` list responses and their current Core field names. */
export function normalizeFileList(payload: unknown): NormalizedFile[] {
  const unwrapped = unwrapApiData<unknown>(payload);
  const values = Array.isArray(unwrapped)
    ? unwrapped
    : isRecord(unwrapped) && Array.isArray(unwrapped.files)
      ? unwrapped.files
      : [];

  return values.flatMap((value) => {
    if (!isRecord(value)) return [];
    const id = stringValue(value.id ?? value.fileId ?? value.file_id);
    if (!id) return [];

    return [{
      ...value,
      id,
      name: stringValue(value.name ?? value.originalName ?? value.original_name),
      mimeType: stringValue(value.mimeType ?? value.mime_type),
      size: Number(value.size ?? value.fileSize ?? value.file_size ?? 0),
      createdAt: stringValue(value.createdAt ?? value.created_at),
      fileUrl: nullableString(value.fileUrl ?? value.file_url) ?? undefined,
    }];
  });
}

function normalizeProjectArtifact(value: unknown): NormalizedProjectArtifact | null {
  if (!isRecord(value)) return null;

  const id = stringValue(value.id ?? value.artifactId ?? value.artifact_id);
  if (!id) return null;

  const fileId = nullableString(value.fileId ?? value.file_id);
  const sessionId = nullableString(value.sessionId ?? value.session_id);
  const explicitSource = value.source;
  const source: NormalizedProjectArtifactSource =
    explicitSource === "project" || explicitSource === "chat" || explicitSource === "generated"
      ? explicitSource
      : fileId
        ? sessionId
          ? "chat"
          : "project"
        : "generated";

  // A file-backed row without a file id cannot be downloaded safely. In
  // particular, never fall back to using the artifact id as a file id.
  if (source !== "generated" && !fileId) return null;

  const rawSize = value.size ?? value.fileSize ?? value.file_size;
  const parsedSize = rawSize === null || rawSize === undefined || rawSize === ""
    ? null
    : Number(rawSize);
  const chatSession = isRecord(value.chat_sessions) ? value.chat_sessions : null;

  return {
    ...value,
    id,
    fileId,
    projectId: nullableString(value.projectId ?? value.project_id),
    source,
    name: stringValue(
      value.name ?? value.originalName ?? value.original_name ?? value.title,
      "Untitled artifact",
    ),
    mimeType: nullableString(value.mimeType ?? value.mime_type),
    size: parsedSize !== null && Number.isFinite(parsedSize) ? parsedSize : null,
    uploadedBy: nullableString(value.uploadedBy ?? value.uploaded_by),
    createdAt: stringValue(value.createdAt ?? value.created_at),
    sessionId,
    agentId: nullableString(
      value.agentId ?? value.agent_id ?? chatSession?.agent_id ?? chatSession?.agentId,
    ),
    artifactType: nullableString(value.artifactType ?? value.artifact_type ?? value.type),
  };
}

/** Normalize the paginated Core artifact list without conflating artifact and file ids. */
export function normalizeProjectArtifactList(payload: unknown): NormalizedProjectArtifact[] {
  const unwrapped = unwrapApiData(payload);

  let values: unknown[] = [];
  if (Array.isArray(unwrapped)) {
    values = unwrapped;
  } else if (isRecord(unwrapped)) {
    if (Array.isArray(unwrapped.artifacts)) values = unwrapped.artifacts;
    else if (Array.isArray(unwrapped.data)) values = unwrapped.data;
  }

  return values
    .map(normalizeProjectArtifact)
    .filter((artifact): artifact is NormalizedProjectArtifact => artifact !== null);
}

/** Translate the UI's legacy session keys to the current Core DTO/entity keys. */
export function toCoreSessionUpdate(updates: Record<string, unknown>): Record<string, unknown> {
  const result = { ...updates };

  if ("is_starred" in result) {
    result.isStarred = result.is_starred;
    delete result.is_starred;
  }
  if ("project_id" in result) {
    const projectId = result.project_id;
    result.projectId = projectId === null || projectId === "" ? null : projectId;
    delete result.project_id;
  }
  if ("agent_id" in result) {
    result.agentId = result.agent_id;
    delete result.agent_id;
  }

  return result;
}
